/**
 * TDRSS relay windows, ported from the pure part of loader-tdrss.js:
 * an S-band LEO mission can relay through a GEO TDRS whenever the straight
 * line between them clears a 6371 km sphere. Positions come from satellite.js
 * (mission TLE) and the fixed lon/lat/altKm of each TDRS.
 */
import * as satellite from 'satellite.js';
import type { ContactWindowMap, Mission, TdrsSatellite } from '@/features/loader/types';
import type { RelayWindow, RelayWindowMap } from './types';

export const RELAY_EARTH_RADIUS_KM = 6371;

export interface GeodeticPosition {
  lat: number;
  lon: number;
  altKm: number;
}

/** Sub-satellite point and altitude of a propagated satrec at timeMs, null when SGP4 fails. */
export function geodeticPositionAt(satrec: satellite.SatRec, timeMs: number): GeodeticPosition | null {
  const date = new Date(timeMs);
  const posVel = satellite.propagate(satrec, date);
  if (!posVel || !posVel.position) return null;
  const gmst = satellite.gstime(date);
  const geo = satellite.eciToGeodetic(posVel.position, gmst);
  return {
    lat: satellite.degreesLat(geo.latitude),
    lon: satellite.degreesLong(geo.longitude),
    altKm: geo.height,
  };
}

/**
 * Relay windows for every mission (missionId -> tdrsId -> windows). Missions
 * without a TLE, deep-space missions and non S-band missions get an empty
 * object; every enabled TDRS gets an array, possibly empty.
 */
export function computeRelayWindows(
  tdrss: TdrsSatellite[],
  missions: Mission[],
  startMs: number,
  endMs: number,
  stepSeconds = 60,
): RelayWindowMap {
  const result: RelayWindowMap = {};
  for (const mission of missions) {
    result[mission.id] = relayWindowsForMission(tdrss, mission, startMs, endMs, stepSeconds);
  }
  return result;
}

/** Relay windows of one mission through each enabled TDRS, sampled every stepSeconds. */
export function relayWindowsForMission(
  tdrss: TdrsSatellite[],
  mission: Mission,
  startMs: number,
  endMs: number,
  stepSeconds = 60,
): Record<string, RelayWindow[]> {
  const result: Record<string, RelayWindow[]> = {};

  if (!mission.orbit || !mission.orbit.tle) return result;
  if (mission.orbit.type === 'deep_space') return result;
  if (mission.requirements.bandType !== 'S-band') return result;

  const satrec = satellite.twoline2satrec(mission.orbit.tle.line1, mission.orbit.tle.line2);
  const stepMs = stepSeconds * 1000;

  for (const tdrs of tdrss) {
    if (!tdrs.enabled) continue;
    const windows: RelayWindow[] = [];
    result[tdrs.id] = windows;

    let inRelay = false;
    let windowStart = 0;
    let current = startMs;

    const close = (endAt: number) => {
      windows.push({
        startMs: windowStart,
        endMs: endAt,
        maxElevation: 45,
        durationMin: Math.round((endAt - windowStart) / 60000),
        relay: true,
        relayId: tdrs.id,
        service: 'SA',
      });
    };

    while (current <= endMs) {
      const satPos = geodeticPositionAt(satrec, current);
      if (satPos) {
        const visible = checkRelayVisibility(satPos, tdrs.position);
        if (visible && !inRelay) {
          inRelay = true;
          windowStart = current;
        } else if (!visible && inRelay) {
          inRelay = false;
          close(current);
        }
      }
      current += stepMs;
    }

    // An open window closes one step past the end, as the original did.
    if (inRelay) close(current);
  }

  return result;
}

/**
 * Line of sight between a LEO position and a TDRS: the segment from the
 * satellite to the TDRS must not cross the 6371 km sphere (ray-sphere test on
 * geocentric spherical coordinates).
 */
export function checkRelayVisibility(
  satPos: GeodeticPosition,
  tdrssPos: { lon: number; lat?: number; altKm?: number },
): boolean {
  const satR = RELAY_EARTH_RADIUS_KM + satPos.altKm;
  const satLatRad = (satPos.lat * Math.PI) / 180;
  const satLonRad = (satPos.lon * Math.PI) / 180;

  const tdrssR = RELAY_EARTH_RADIUS_KM + (tdrssPos.altKm || 0);
  const tdrssLatRad = ((tdrssPos.lat || 0) * Math.PI) / 180;
  const tdrssLonRad = (tdrssPos.lon * Math.PI) / 180;

  const sat = {
    x: satR * Math.cos(satLatRad) * Math.cos(satLonRad),
    y: satR * Math.cos(satLatRad) * Math.sin(satLonRad),
    z: satR * Math.sin(satLatRad),
  };
  const tdrs = {
    x: tdrssR * Math.cos(tdrssLatRad) * Math.cos(tdrssLonRad),
    y: tdrssR * Math.cos(tdrssLatRad) * Math.sin(tdrssLonRad),
    z: tdrssR * Math.sin(tdrssLatRad),
  };

  const dx = tdrs.x - sat.x;
  const dy = tdrs.y - sat.y;
  const dz = tdrs.z - sat.z;

  // |sat + t * d|^2 = R^2 as A t^2 + B t + C = 0.
  const A = dx * dx + dy * dy + dz * dz;
  const B = 2 * (sat.x * dx + sat.y * dy + sat.z * dz);
  const C = sat.x * sat.x + sat.y * sat.y + sat.z * sat.z - RELAY_EARTH_RADIUS_KM * RELAY_EARTH_RADIUS_KM;

  const discriminant = B * B - 4 * A * C;
  if (discriminant < 0) return true;

  const sqrtDisc = Math.sqrt(discriminant);
  const t1 = (-B - sqrtDisc) / (2 * A);
  const t2 = (-B + sqrtDisc) / (2 * A);

  // Both intersections outside the segment and on the same side: clear.
  if ((t1 < 0 || t1 > 1) && (t2 < 0 || t2 > 1)) {
    if (t1 < 0 && t2 < 0) return true;
    if (t1 > 1 && t2 > 1) return true;
  }

  return false;
}

/** Ground footprint radius (km, arc length) of a GEO satellite at altKm. */
export function computeFootprintRadiusKm(altKm: number): number {
  const halfAngleRad = Math.asin(RELAY_EARTH_RADIUS_KM / (RELAY_EARTH_RADIUS_KM + altKm));
  const centralAngle = Math.PI / 2 - halfAngleRad;
  return RELAY_EARTH_RADIUS_KM * centralAngle;
}

/**
 * Add relay windows to an analysis contact-window map under the TDRS id (only
 * TDRS with at least one window), mutating the map. Returns the number of
 * windows added. Relay windows do not take part in the allocation.
 */
export function mergeRelayWindows(contactWindows: ContactWindowMap, relayWindows: RelayWindowMap): number {
  let added = 0;
  for (const missionId of Object.keys(relayWindows)) {
    const byTdrs = relayWindows[missionId];
    for (const tdrsId of Object.keys(byTdrs)) {
      const windows = byTdrs[tdrsId];
      if (windows.length === 0) continue;
      if (!contactWindows[missionId]) contactWindows[missionId] = {};
      contactWindows[missionId][tdrsId] = windows;
      added += windows.length;
    }
  }
  return added;
}
